import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { useLocation } from 'react-router-dom'

import { Page } from '@templates'
import {
  Document,
  Environment,
  isReadyToSendDocumentFiles,
  normalizeDocumentFiles,
  type AcceptedDocumentType,
} from '@interco/inter-frontend-liveness-lib'
import {
  setAnexosFoto,
  sendToS3Foto,
  setIsErrorFoto,
} from '@store/refinanciamento/fotoDocumentos/actions'
import { ApplicationState } from '@store/types'
import routes from '@routes/routes'
import { Colors } from '@utils/Colors'
import { BridgeService } from '@services/bridge'
import { WbEnvironments } from '@interco/inter-webview-bridge'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { StepRefin } from '@utils/enums'
import { shouldFallbackToEnvSerasaSdk } from '@utils/serasaSdkFallback'
import { useTrackingRefin } from '@hooks'

import { Container, DocumentGlobalStyles } from './styles'

const RESTRICTED_DOCS_BY_CONVENIO: Record<
  string,
  AcceptedDocumentType | readonly AcceptedDocumentType[]
> = {}

const DocumentoIdentificacao = () => {
  useTrackingRefin(StepRefin.DOCUMENTO_IDENTIFICACAO)

  const dispatch = useDispatch()
  const location = useLocation()

  const { accessToken } = useSelector((state: ApplicationState) => state.session)
  const { numeroProposta } = useSelector(
    (state: ApplicationState) => state.refinanciamento.proposta,
  )
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )

  const [isPageLoading, setIsPageLoading] = useState(() => {
    if (typeof document === 'undefined') return false
    return document.readyState !== 'complete'
  })
  const [useEnvSdkUrl, setUseEnvSdkUrl] = useState(false)

  const documentComponentKey = useMemo(
    () => `${location.key || 'documento'}-${uuidv4()}`,
    [location.key],
  )

  const { isDone } = useSelector((state: ApplicationState) => state.refinanciamento.fotoDocumentos)

  const handleCaptureError = useCallback(
    (error?: { code?: string | number }) => {
      if (shouldFallbackToEnvSerasaSdk(error, useEnvSdkUrl)) {
        setUseEnvSdkUrl(true)
        return
      }

      dispatch(setIsErrorFoto(true))
      window.location.href = routes.STATUS_DOCUMENTO
    },
    [dispatch, useEnvSdkUrl],
  )

  const sendDocumentsPhotosToS3 = useCallback(
    async (documents: string[]) => {
      const files = await normalizeDocumentFiles(documents)
      if (!isReadyToSendDocumentFiles(files)) {
        handleCaptureError()
        return
      }

      const anexosToSend = files.map((file) => ({
        anexo: {
          id: uuidv4(),
          nome: `RG/CNH.${file.extension}`,
          tipoExtensao: file.extension,
          blobs: [
            {
              height: file.height,
              width: file.width,
              data: file.data,
            },
          ],
        },
        tipo: 'RG_CNH',
      }))

      dispatch(
        sendToS3Foto({
          tipo: 'RG_CNH',
          anexos: anexosToSend,
          callbackError: () => {
            dispatch(setAnexosFoto([]))
            window.location.href = routes.STATUS_DOCUMENTO
          },
        }),
      )
    },
    [dispatch, handleCaptureError],
  )

  useEffect(() => {
    if (isDone) window.location.href = routes.STATUS_DOCUMENTO
  }, [dispatch, isDone])

  // Hide SDK loading on iOS until window load completes
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (document.readyState === 'complete') {
      setIsPageLoading(false)
      return undefined
    }

    const handleLoad = () => setIsPageLoading(false)
    window.addEventListener('load', handleLoad, { once: true })

    return () => {
      window.removeEventListener('load', handleLoad)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const handleBrowserBack = () => {
      window.location.href = routes.STATUS_DOCUMENTO
    }

    window.addEventListener('popstate', handleBrowserBack)

    return () => {
      window.removeEventListener('popstate', handleBrowserBack)
    }
  }, [])

  const shouldHideLoading =
    BridgeService.getEnvironment() === WbEnvironments.IOS && isPageLoading === true

  return (
    <Page>
      <DocumentGlobalStyles />
      <Container>
        <Document
          enablePermissionsBottomSheet
          proposalCode={String(numeroProposta) || ''}
          key={`${documentComponentKey}-${useEnvSdkUrl ? 'env-sdk' : 'bff-sdk'}`}
          accessToken={accessToken || ''}
          hideLoading={shouldHideLoading}
          origin={BridgeService.isBrowser() ? 'REFINANCING_WEB' : 'REFINANCING_APP'}
          convenio={convenioCodigo}
          restrictedDocsByConvenio={RESTRICTED_DOCS_BY_CONVENIO}
          useEnvSdkUrl={useEnvSdkUrl}
          callSuccessAction={(result) => {
            dispatch(trackingRefin(StepRefin.CAPTURA_DOCUMENTO_SERASA_SUCESSO))

            sendDocumentsPhotosToS3(result.images as string[])
          }}
          callErrorAction={handleCaptureError}
          containerId="document-container"
          customDocumentOptions={{
            documentScan: {
              captureScreen: {
                manualButtonTimeout: 3,
              },
              fileUploading: {
                loaderColor: Colors.PRIMARY500,
              },
            },
          }}
          loadingProps={{
            width: 90,
            height: 90,
          }}
          environment={process.env.REACT_APP_ENVIROMENT as Environment}
        />
      </Container>
    </Page>
  )
}

export default DocumentoIdentificacao
